<template>
  <div>
    <define-segmentation v-slot="{ invisible, segmentContent }">
      <component
        :is="`lazy-cms-${segmentContent.componentType}`"
        v-if="segmentContent.componentType"
        :key="segmentContent.id"
        :class="{ invisible, 'h-full': fullHeight }"
        :content="segmentContent"
        :data-test-id="`cms-${segmentContent.componentType}-${segmentContent.id}`"
        :first-element="firstElement"
      />
    </define-segmentation>

    <client-only>
      <div
        v-for="segmentContent in segmentContents"
        :key="segmentContent.id"
        v-style:display="getVisibilityStyles(segmentContent.hideOnBreakpoint)"
        :class="{ 'container': hasContainer(segmentContent), 'h-full': fullHeight }"
      >
        <segmentation-template :segment-content />
      </div>
      <template v-if="!$feature.enableCSROnlyForSegmentation" #fallback>
        <div
          v-for="segmentContent in segmentContents"
          :key="segmentContent.id"
          v-style:display="getVisibilityStyles(segmentContent.hideOnBreakpoint)"
          class="skeleton"
          :class="{ container: hasContainer(segmentContent) }"
        >
          <segmentation-template :invisible="true" :segment-content />
        </div>
      </template>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { Segmentation, SegmentationContent } from '#types/components/cms/segmentation'
import { SectionContextKey } from '#content/contexts/section'
import mappings from '#content/mappings'

const props = defineProps<{
  content: Segmentation
  firstElement?: boolean
}>()

const { fullHeight } = inject(SectionContextKey, {} as SectionProvider)

const auth = useAuthStore()
const { isLoyaltyMember, isWranxProfile, isIpaProfile, isWranxOrIpaProfile, isAthleteProfile }
  = storeToRefs(useUserStore())
const cart = useCartStore()
const query = useRouteQuery('p13n_testcontext', '{}')

const [DefineSegmentation, SegmentationTemplate] = createReusableTemplate()

const segmentsMap = computed(() => ({
  'loggedout': !auth.loggedIn,
  'loggedin': auth.loggedIn,
  'loyalty-loggedin': isLoyaltyMember.value,
  'non-loyalty-loggedin': auth.loggedIn && !isLoyaltyMember.value,
  'employee-loggedin': auth.loggedIn && auth.isEmployee,
  'non-employee-loggedin': auth.loggedIn && !auth.isEmployee,
  'cart-empty': !cart.totalItems,
  'cart-not-empty': cart.totalItems,
  'ipa-wranx-loggedin': auth.loggedIn && isWranxOrIpaProfile.value,
  'non-ipa-wranx-loggedin': auth.loggedIn && !isWranxOrIpaProfile.value,
  'wranx-loggedin': auth.loggedIn && isWranxProfile.value,
  'non-wranx-loggedin': auth.loggedIn && !isWranxProfile.value,
  'ipa-loggedin': auth.loggedIn && isIpaProfile.value,
  'non-ipa-loggedin': auth.loggedIn && !isIpaProfile.value,
  'athletes-loggedin': auth.loggedIn && isAthleteProfile.value,
  'non-athletes-loggedin': auth.loggedIn && !isAthleteProfile.value,
}))

// override can be set in the CMS to preview specific segments
const segmentOverride = computed(() => JSON.parse(query.value)?.segment)

const segmentContents: Ref<SegmentationContent[]> = computed(() => {
  const segment = segmentOverride.value
    ? props.content.segments?.find(({ id }) => id === segmentOverride.value)
    : props.content.segments?.find(({ id }) => segmentsMap.value[id])
  return (segment?.targets
    || props.content.baselines
    || [] as SegmentationContent[]
  ).map((segmentationContent) => ({
    ...resolveContentComponent(segmentationContent, mappings),
    sectionId: props.content.id
  } as SegmentationContent))
})

/**
 * If the parent component is manually (!) set as full width we should take this as source for applying container class
 * Otherwise we should look into direct segment component configuration
 */
const hasContainer = (segmentContent: SegmentationContent) => {
  if (typeof props.content !== 'undefined' && props.content.isFullWidth !== undefined)
    return !props.content.isFullWidth

  return !segmentContent.isFullWidth
}

const emptySections = useState<Record<string, boolean>>('emptySections', () => ({}))

watch(segmentContents, (contents) => {
  emptySections.value[props.content.id] = !contents.length
}, { immediate: true })
</script>
